import { useEffect, useRef, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, Legend } from 'recharts';
import {
  ScanSearch, ArrowLeft, Upload, Trash2, FileText, Image as ImageIcon,
  Play, Loader2, AlertTriangle, TrendingUp, TrendingDown, ShieldCheck, XCircle,
  RefreshCw, HelpCircle, CheckCircle2, Printer, Download, MessageSquare, History, Wallet, BarChart3,
} from 'lucide-react';

const AFF_STYLE = {
  OK:            'bg-emerald-950 border-emerald-600 text-emerald-300',
  BORDERLINE:    'bg-amber-950 border-amber-600 text-amber-300',
  'HIGH RISK':   'bg-rose-950 border-rose-600 text-rose-300',
  'OVER-INDEBTED': 'bg-rose-950 border-rose-700 text-rose-200',
  UNKNOWN:       'bg-slate-800 border-slate-600 text-slate-300',
};

const API_URL = process.env.REACT_APP_BACKEND_URL;
const R = (n) => 'R ' + Number(n || 0).toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const ACTIVITY_STYLE = {
  NONE:     { pill: 'bg-slate-800 border-slate-600 text-slate-300', ring: 'border-slate-700' },
  LOW:      { pill: 'bg-emerald-950 border-emerald-600 text-emerald-300', ring: 'border-emerald-800' },
  MODERATE: { pill: 'bg-amber-950 border-amber-600 text-amber-300', ring: 'border-amber-800' },
  HIGH:     { pill: 'bg-rose-950 border-rose-600 text-rose-300', ring: 'border-rose-800' },
};

const CLASSIFICATION_OPTIONS = [
  { v: 'loan_inflow', l: 'Loan Inflow' },
  { v: 'loan_repayment', l: 'Loan Repayment' },
  { v: 'failed_debit', l: 'Failed Loan Debit' },
  { v: 'salary', l: 'Salary' },
  { v: 'own_transfer', l: 'Own Transfer' },
  { v: 'refund', l: 'Refund' },
  { v: 'non_loan', l: 'Non-Loan' },
];

const docTypeLabel = (t) => t === 'credit_report' ? 'Credit Report'
  : t === 'quinnpay' ? 'QuinnPay'
  : 'Bank Statement';

export default function VettingCase() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [caseData, setCaseData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [analysing, setAnalysing] = useState(false);
  const [finalising, setFinalising] = useState(false);
  const fileInputRef = useRef(null);
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/api/vetting/cases/${id}`, { headers });
      setCaseData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load case');
    } finally { setLoading(false); }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const [uploadDocType, setUploadDocType] = useState('bank_statement');
  const uploadFiles = async (fileList) => {
    if (!fileList || fileList.length === 0) return;
    setUploading(true);
    try {
      for (const file of fileList) {
        const fd = new FormData();
        fd.append('file', file);
        fd.append('doc_type', uploadDocType);
        await axios.post(`${API_URL}/api/vetting/cases/${id}/documents`, fd, {
          headers: { ...headers, 'Content-Type': 'multipart/form-data' },
        });
      }
      toast.success('Documents uploaded');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const removeDoc = async (docId) => {
    if (!window.confirm('Remove this document?')) return;
    try {
      await axios.delete(`${API_URL}/api/vetting/cases/${id}/documents/${docId}`, { headers });
      toast.success('Document removed');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Remove failed'); }
  };

  const runAnalysis = async () => {
    setAnalysing(true);
    try {
      // Fire-and-forget on backend; response returns instantly with status='analysing'.
      await axios.post(`${API_URL}/api/vetting/cases/${id}/analyse`, {}, { headers, timeout: 30000 });
      toast.info('Analysis running… this can take 30–90 s per document.');
      // Poll case status every 5 s until done or error
      const start = Date.now();
      const poll = async () => {
        try {
          const r = await axios.get(`${API_URL}/api/vetting/cases/${id}`, { headers });
          setCaseData(r.data);
          if (r.data.status === 'analysed') {
            toast.success('Analysis complete');
            setAnalysing(false);
            return;
          }
          if (r.data.analysis_error) {
            toast.error(r.data.analysis_error);
            setAnalysing(false);
            return;
          }
          if (Date.now() - start > 6 * 60 * 1000) {
            toast.error('Analysis is taking too long — check back later');
            setAnalysing(false);
            return;
          }
          setTimeout(poll, 5000);
        } catch {
          setTimeout(poll, 5000);
        }
      };
      setTimeout(poll, 5000);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Analysis failed to start');
      setAnalysing(false);
    }
  };

  const finalise = async () => {
    setFinalising(true);
    try {
      await axios.post(`${API_URL}/api/vetting/cases/${id}/finalise`, {}, { headers });
      toast.success('Case finalised');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Finalise failed'); }
    finally { setFinalising(false); }
  };

  const overrideTxn = async (txnId, patch) => {
    try {
      await axios.patch(`${API_URL}/api/vetting/cases/${id}/transactions/${txnId}`, patch, { headers });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Update failed'); }
  };

  const downloadPdf = async () => {
    try {
      const r = await axios.get(`${API_URL}/api/vetting/cases/${id}/pdf`, { headers, responseType: 'blob' });
      const url = window.URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `vetting-${caseData.applicant_name || id}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'PDF export failed');
    }
  };

  const addNote = async (text) => {
    if (!text.trim()) return;
    try {
      await axios.post(`${API_URL}/api/vetting/cases/${id}/notes`, { text }, { headers });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Note failed'); }
  };

  const removeNote = async (nid) => {
    try {
      await axios.delete(`${API_URL}/api/vetting/cases/${id}/notes/${nid}`, { headers });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Delete failed'); }
  };

  if (loading || !caseData) return <DashboardLayout><div className="text-slate-400 text-center py-20">Loading case…</div></DashboardLayout>;

  const a = caseData.assessment || {};
  const activityKey = a.borrowing_activity || 'NONE';
  const style = ACTIVITY_STYLE[activityKey] || ACTIVITY_STYLE.NONE;
  const docs = caseData.documents || [];
  const analysis = caseData.analysis;

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="vetting-case">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <button onClick={() => navigate('/vetting')} className="text-slate-400 hover:text-emerald-400 text-sm flex items-center gap-1 mb-1" data-testid="back-to-vetting-list">
              <ArrowLeft className="h-3 w-3" /> Back to Vetting
            </button>
            <h1 className="text-2xl font-bold text-white flex items-center gap-2">
              <ScanSearch className="h-6 w-6 text-emerald-400" />
              {caseData.applicant_name}
              {caseData.id_number && <span className="text-slate-500 text-sm font-mono ml-2">{caseData.id_number}</span>}
            </h1>
            <div className="text-xs text-slate-500 mt-1">Salary R {Number(caseData.monthly_salary || 0).toLocaleString()} · payday day {caseData.salary_payday} · status <span className="uppercase text-emerald-400">{caseData.status}</span></div>
          </div>
          <div className="flex items-center gap-2">
            <Button onClick={() => window.print()} variant="outline" className="bg-slate-800 border-slate-700 text-white" data-testid="print-vetting-btn"><Printer className="h-4 w-4 mr-1" />Print</Button>
            {caseData.assessment && (
              <Button onClick={downloadPdf} variant="outline" className="bg-slate-800 border-slate-700 text-white" data-testid="download-pdf-btn"><Download className="h-4 w-4 mr-1" />PDF</Button>
            )}
            <Button onClick={runAnalysis} disabled={analysing || docs.length === 0} className="bg-emerald-600 hover:bg-emerald-700" data-testid="run-analysis-btn">
              {analysing ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" />Analysing…</> : <><Play className="h-4 w-4 mr-1" />Run Analysis</>}
            </Button>
            {caseData.assessment && caseData.status !== 'finalised' && (
              <Button onClick={finalise} disabled={finalising} className="bg-blue-600 hover:bg-blue-700" data-testid="finalise-btn">
                {finalising ? 'Finalising…' : (<><CheckCircle2 className="h-4 w-4 mr-1" />Finalise</>)}
              </Button>
            )}
          </div>
        </div>

        {/* Section 12 headline */}
        {a.final_report && (
          <Card className={`bg-slate-900/60 ${style.ring} border-2`} data-testid="borrowing-headline">
            <CardContent className="p-4 md:p-5 grid grid-cols-2 sm:grid-cols-3 md:flex md:flex-wrap md:items-center gap-4 md:gap-6">
              <div className="col-span-2 sm:col-span-3 md:col-span-1">
                <div className="text-xs uppercase tracking-wider text-slate-400">Borrowing Activity</div>
                <div className="text-2xl md:text-3xl font-bold text-white">{activityKey}</div>
                <div className="text-xs text-slate-500 mt-1">{a.borrowing_risk_reason}</div>
              </div>
              <div className="md:border-l md:border-slate-700 md:pl-6">
                <div className="text-xs text-slate-500">Total Lenders</div>
                <div className="text-xl md:text-2xl font-bold text-white">{a.final_report.total_lenders}</div>
              </div>
              <div>
                <div className="text-xs text-slate-500">Loan Deposits</div>
                <div className="text-xl md:text-2xl font-bold text-emerald-400">{a.final_report.loan_deposits}</div>
              </div>
              <div>
                <div className="text-xs text-slate-500">Total Deposit Value</div>
                <div className="text-lg md:text-2xl font-bold text-emerald-400">{R(a.final_report.total_loan_deposit_value)}</div>
              </div>
              <div>
                <div className="text-xs text-slate-500">Monthly Repayments</div>
                <div className="text-lg md:text-2xl font-bold text-rose-400">{R(a.final_report.monthly_bank_loan_repayments)}</div>
              </div>
              <div>
                <div className="text-xs text-slate-500">Loan Stacking</div>
                <Badge className={`text-sm ${a.final_report.loan_stacking ? 'bg-rose-950 border-rose-600 text-rose-300' : 'bg-emerald-950 border-emerald-600 text-emerald-300'}`}>{a.final_report.loan_stacking ? 'YES' : 'NO'}</Badge>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Affordability & Debt Orders */}
        {a.affordability && (
          <Card className="bg-slate-800/50 border-slate-700" data-testid="affordability-card">
            <CardHeader><CardTitle className="text-white text-lg flex items-center gap-2"><Wallet className="h-5 w-5 text-blue-400" /> Affordability & Debt Orders</CardTitle></CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-6 gap-3 text-sm mb-4">
                <FinalStat label="Salary" value={R(a.affordability.monthly_salary)} />
                <FinalStat label="Bank Debt Orders" value={R(a.affordability.bank_debt_orders_total)} />
                <FinalStat label="Recent Arrears" value={R(a.affordability.recent_arrears_total)} />
                <FinalStat label="Available" value={R(a.affordability.available_after_obligations)} />
                <FinalStat label="Utilisation" value={`${a.affordability.utilisation_pct ?? 0}%`} />
                <div className="bg-slate-800/60 border border-slate-700 rounded p-3">
                  <div className="text-[10px] uppercase tracking-wider text-slate-400">Verdict</div>
                  <div className="mt-1"><Badge className={`text-sm border ${AFF_STYLE[a.affordability.verdict] || AFF_STYLE.UNKNOWN}`}>{a.affordability.verdict}</Badge></div>
                </div>
              </div>
              {(a.debt_orders || []).length > 0 && (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm text-slate-200" data-testid="debt-orders-table">
                    <thead className="text-xs text-slate-400 border-b border-slate-700">
                      <tr>
                        <th className="text-left py-1.5 px-2">Lender</th>
                        <th className="text-right py-1.5 px-2">Instalment</th>
                        <th className="text-center py-1.5 px-2">Occurrences</th>
                        <th className="text-center py-1.5 px-2">Frequency</th>
                      </tr>
                    </thead>
                    <tbody>
                      {a.debt_orders.map((d, i) => (
                        <tr key={i} className="border-b border-slate-800">
                          <td className="py-1.5 px-2 font-medium">{d.lender}</td>
                          <td className="py-1.5 px-2 text-right text-rose-400">{R(d.amount)}</td>
                          <td className="py-1.5 px-2 text-center">{d.occurrences}</td>
                          <td className="py-1.5 px-2 text-center text-xs">{d.frequency || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Documents */}
        <Card className="bg-slate-800/50 border-slate-700">
          <CardHeader><CardTitle className="text-white text-lg flex items-center gap-2"><FileText className="h-5 w-5" /> Documents</CardTitle></CardHeader>
          <CardContent>
            <div className="flex items-center gap-3 mb-4 flex-wrap">
              <Select value={uploadDocType} onValueChange={setUploadDocType}>
                <SelectTrigger className="h-9 w-48 bg-slate-900 border-slate-700 text-white text-xs" data-testid="doc-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="bank_statement">Bank Statement</SelectItem>
                  <SelectItem value="credit_report">Credit Report</SelectItem>
                  <SelectItem value="quinnpay">QuinnPay Report</SelectItem>
                </SelectContent>
              </Select>
              <input ref={fileInputRef} type="file" accept="application/pdf,image/png,image/jpeg,image/webp" multiple className="hidden" onChange={(e) => uploadFiles(e.target.files)} data-testid="doc-upload-input" />
              <Button onClick={() => fileInputRef.current?.click()} disabled={uploading || docs.length >= 6} className="bg-blue-600 hover:bg-blue-700" data-testid="upload-doc-btn">
                {uploading ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" />Uploading…</> : <><Upload className="h-4 w-4 mr-1" />Upload PDF / Image</>}
              </Button>
              <span className="text-xs text-slate-500">{docs.length}/6 · PDF or image, max 10 MB each</span>
            </div>
            {docs.length === 0 ? <div className="text-center py-6 text-slate-500 text-sm">No documents yet. Upload 1–3 months of bank statements, plus optional credit report / QuinnPay.</div>
              : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {docs.map(d => (
                    <div key={d.id} className="bg-slate-900 border border-slate-700 rounded p-3 flex items-center gap-2" data-testid={`doc-item-${d.id}`}>
                      {d.kind === 'pdf' ? <FileText className="h-6 w-6 text-rose-400 shrink-0" /> : <ImageIcon className="h-6 w-6 text-blue-400 shrink-0" />}
                      <div className="flex-1 min-w-0">
                        <div className="text-sm text-white truncate">{d.filename}</div>
                        <div className="text-[10px] text-slate-500">{docTypeLabel(d.doc_type)} · {(d.size / 1024).toFixed(0)} KB · {d.uploaded_by}</div>
                      </div>
                      <Button size="sm" variant="destructive" className="h-7 w-7 p-0" onClick={() => removeDoc(d.id)} data-testid={`remove-doc-${d.id}`}><Trash2 className="h-3 w-3" /></Button>
                    </div>
                  ))}
                </div>
              )}
          </CardContent>
        </Card>

        {/* Assessment blocks — only after analysis */}
        {analysis ? (
          <>
            {/* Section 2: Loan Inflows */}
            <MonthlyLoanChart inflows={a.inflows || []} repayments={a.repayments || []} />
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader><CardTitle className="text-white text-lg flex items-center gap-2"><TrendingUp className="h-5 w-5 text-emerald-400" /> Loan Inflows ({(a.inflows || []).length})</CardTitle></CardHeader>
              <CardContent>
                {(a.inflows || []).length === 0 ? <div className="text-slate-500 text-sm text-center py-4">No loan inflows detected.</div>
                  : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm text-slate-200" data-testid="inflows-table">
                        <thead className="text-xs text-slate-400 border-b border-slate-700">
                          <tr>
                            <th className="text-left py-2 px-2">Lender</th>
                            <th className="text-left py-2 px-2">Deposit Date</th>
                            <th className="text-right py-2 px-2">Amount</th>
                            <th className="text-center py-2 px-2">Salary Cycle</th>
                            <th className="text-center py-2 px-2">Days after Salary</th>
                            <th className="text-center py-2 px-2">Confidence</th>
                            <th className="text-center py-2 px-2">Assessment</th>
                          </tr>
                        </thead>
                        <tbody>
                          {a.inflows.map(i => (
                            <tr key={i.transaction_id} className="border-b border-slate-800">
                              <td className="py-1.5 px-2 font-medium">{i.lender}</td>
                              <td className="py-1.5 px-2">{i.deposit_date}</td>
                              <td className="py-1.5 px-2 text-right text-emerald-400">{R(i.amount)}</td>
                              <td className="py-1.5 px-2 text-center text-xs">{i.salary_cycle || '—'}</td>
                              <td className="py-1.5 px-2 text-center">{i.days_after_salary ?? '—'}</td>
                              <td className="py-1.5 px-2 text-center">
                                <Badge className={`text-[10px] ${i.confidence === 'high' ? 'bg-emerald-950 border-emerald-600 text-emerald-300' : i.confidence === 'medium' ? 'bg-amber-950 border-amber-600 text-amber-300' : 'bg-slate-800 border-slate-600 text-slate-300'}`}>{i.confidence}</Badge>
                              </td>
                              <td className="py-1.5 px-2 text-center text-xs">{i.assessment}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
              </CardContent>
            </Card>

            {/* Section 3: Loan Repayments */}
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader><CardTitle className="text-white text-lg flex items-center gap-2"><TrendingDown className="h-5 w-5 text-rose-400" /> Loan Repayments ({(a.repayments || []).length})</CardTitle></CardHeader>
              <CardContent>
                {(a.repayments || []).length === 0 ? <div className="text-slate-500 text-sm text-center py-4">No loan repayments detected.</div>
                  : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm text-slate-200" data-testid="repayments-table">
                        <thead className="text-xs text-slate-400 border-b border-slate-700">
                          <tr>
                            <th className="text-left py-2 px-2">Lender</th>
                            <th className="text-left py-2 px-2">Reference</th>
                            <th className="text-left py-2 px-2">Debit Date</th>
                            <th className="text-right py-2 px-2">Amount</th>
                            <th className="text-center py-2 px-2">Frequency</th>
                            <th className="text-center py-2 px-2">Status</th>
                            <th className="text-center py-2 px-2">Class</th>
                          </tr>
                        </thead>
                        <tbody>
                          {a.repayments.map(r => (
                            <tr key={r.transaction_id} className="border-b border-slate-800">
                              <td className="py-1.5 px-2 font-medium">{r.lender}</td>
                              <td className="py-1.5 px-2 text-xs text-slate-400 truncate max-w-[240px]">{r.reference}</td>
                              <td className="py-1.5 px-2">{r.debit_date}</td>
                              <td className="py-1.5 px-2 text-right text-rose-400">{R(r.amount)}</td>
                              <td className="py-1.5 px-2 text-center text-xs">{r.frequency}</td>
                              <td className="py-1.5 px-2 text-center">
                                {r.successful && <ShieldCheck className="h-4 w-4 text-emerald-400 inline" />}
                                {r.failed && <XCircle className="h-4 w-4 text-rose-400 inline" />}
                              </td>
                              <td className="py-1.5 px-2 text-center text-[10px] uppercase">{r.classification}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
              </CardContent>
            </Card>

            {/* Section 10: Loan Stacking */}
            {a.stacking_rows && a.stacking_rows.length > 0 && (
              <Card className="bg-slate-800/50 border-slate-700">
                <CardHeader><CardTitle className="text-white text-lg flex items-center gap-2"><AlertTriangle className="h-5 w-5 text-amber-400" /> Loan Stacking by Salary Cycle</CardTitle></CardHeader>
                <CardContent>
                  <table className="w-full text-sm text-slate-200" data-testid="stacking-table">
                    <thead className="text-xs text-slate-400 border-b border-slate-700">
                      <tr>
                        <th className="text-left py-2 px-2">Salary Cycle</th>
                        <th className="text-center py-2 px-2">Lenders</th>
                        <th className="text-center py-2 px-2">Deposits</th>
                        <th className="text-right py-2 px-2">Total Value</th>
                        <th className="text-center py-2 px-2">Stacking</th>
                      </tr>
                    </thead>
                    <tbody>
                      {a.stacking_rows.map(s => (
                        <tr key={s.salary_cycle} className="border-b border-slate-800">
                          <td className="py-1.5 px-2">{s.salary_cycle}</td>
                          <td className="py-1.5 px-2 text-center">{s.lenders_detected}</td>
                          <td className="py-1.5 px-2 text-center">{s.loan_deposits}</td>
                          <td className="py-1.5 px-2 text-right">{R(s.total_value)}</td>
                          <td className="py-1.5 px-2 text-center">
                            <Badge className={s.loan_stacking ? 'bg-rose-950 border-rose-600 text-rose-300' : 'bg-emerald-950 border-emerald-600 text-emerald-300'}>{s.loan_stacking ? 'YES' : 'no'}</Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </CardContent>
              </Card>
            )}

            {/* Section 8: All Detected Loans (bank-only view) */}
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader><CardTitle className="text-white text-lg">All Detected Loans — Bank Only ({(a.all_detected_loans || []).length})</CardTitle></CardHeader>
              <CardContent>
                <table className="w-full text-sm text-slate-200" data-testid="all-loans-table">
                  <thead className="text-xs text-slate-400 border-b border-slate-700">
                    <tr>
                      <th className="text-left py-2 px-2">Lender</th>
                      <th className="text-right py-2 px-2">Loan Received</th>
                      <th className="text-right py-2 px-2">Monthly Repayment</th>
                      <th className="text-center py-2 px-2">Frequency</th>
                      <th className="text-center py-2 px-2">Status</th>
                      <th className="text-center py-2 px-2">Confidence</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(a.all_detected_loans || []).map((l, idx) => (
                      <tr key={idx} className="border-b border-slate-800">
                        <td className="py-1.5 px-2 font-medium">
                          {l.lender}
                          {l.is_unidentified && <Badge className="ml-2 text-[10px] bg-amber-950 border-amber-600 text-amber-300">UNIDENTIFIED</Badge>}
                        </td>
                        <td className="py-1.5 px-2 text-right text-emerald-400">{R(l.loan_received)}</td>
                        <td className="py-1.5 px-2 text-right text-rose-400">{R(l.repayment_monthly)}</td>
                        <td className="py-1.5 px-2 text-center text-xs">{l.frequency || '—'}</td>
                        <td className="py-1.5 px-2 text-center text-[10px] uppercase">{l.status}</td>
                        <td className="py-1.5 px-2 text-center text-xs">{l.confidence}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>

            {/* Section 6: Cross-source Consolidated Loans */}
            {(a.consolidated_loans || []).length > 0 && (
              <Card className="bg-slate-900/70 border-emerald-800 border-2">
                <CardHeader><CardTitle className="text-white text-lg flex items-center gap-2"><ShieldCheck className="h-5 w-5 text-emerald-400" /> Consolidated Loans — Cross-Source ({a.consolidated_loans.length})</CardTitle></CardHeader>
                <CardContent>
                  <p className="text-xs text-slate-400 mb-2">One row per lender across Bank, Credit Report and QuinnPay. Duplicate obligations collapsed.</p>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm text-slate-200" data-testid="consolidated-loans-table">
                      <thead className="text-xs text-slate-400 border-b border-slate-700">
                        <tr>
                          <th className="text-left py-2 px-2">Lender</th>
                          <th className="text-center py-2 px-2">Bank</th>
                          <th className="text-center py-2 px-2">Credit</th>
                          <th className="text-center py-2 px-2">QuinnPay</th>
                          <th className="text-right py-2 px-2">Loan Received</th>
                          <th className="text-right py-2 px-2">Monthly Repayment</th>
                          <th className="text-right py-2 px-2">Recent Arrears</th>
                          <th className="text-center py-2 px-2">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {a.consolidated_loans.map((l, idx) => (
                          <tr key={idx} className="border-b border-slate-800">
                            <td className="py-1.5 px-2 font-medium">
                              {l.lender}
                              {l.is_unidentified && <Badge className="ml-2 text-[10px] bg-amber-950 border-amber-600 text-amber-300">UNIDENTIFIED</Badge>}
                            </td>
                            <td className="py-1.5 px-2 text-center">{l.sources?.bank ? <CheckCircle2 className="h-4 w-4 text-emerald-400 inline" /> : <span className="text-slate-600">—</span>}</td>
                            <td className="py-1.5 px-2 text-center">{l.sources?.credit ? <CheckCircle2 className="h-4 w-4 text-blue-400 inline" /> : <span className="text-slate-600">—</span>}</td>
                            <td className="py-1.5 px-2 text-center">{l.sources?.quinnpay ? <CheckCircle2 className="h-4 w-4 text-amber-400 inline" /> : <span className="text-slate-600">—</span>}</td>
                            <td className="py-1.5 px-2 text-right text-emerald-400">{R(l.loan_received)}</td>
                            <td className="py-1.5 px-2 text-right text-rose-400">{R(l.repayment_monthly)}</td>
                            <td className={`py-1.5 px-2 text-right ${l.recent_arrears > 0 ? 'text-rose-400 font-semibold' : 'text-slate-500'}`}>{l.recent_arrears > 0 ? R(l.recent_arrears) : '—'}</td>
                            <td className="py-1.5 px-2 text-center text-[10px] uppercase">{l.status}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Section 4: Credit Report Accounts */}
            {(a.credit_accounts || []).length > 0 && (
              <Card className="bg-slate-800/50 border-slate-700">
                <CardHeader><CardTitle className="text-white text-lg flex items-center gap-2"><FileText className="h-5 w-5 text-blue-400" /> Credit Report Accounts ({a.credit_accounts.length})</CardTitle></CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm text-slate-200" data-testid="credit-accounts-table">
                      <thead className="text-xs text-slate-400 border-b border-slate-700">
                        <tr>
                          <th className="text-left py-2 px-2">Creditor</th>
                          <th className="text-left py-2 px-2">Type</th>
                          <th className="text-left py-2 px-2">Account #</th>
                          <th className="text-center py-2 px-2">Open Date</th>
                          <th className="text-center py-2 px-2">Status</th>
                          <th className="text-right py-2 px-2">Balance</th>
                          <th className="text-right py-2 px-2">Arrears</th>
                          <th className="text-center py-2 px-2">Recent</th>
                        </tr>
                      </thead>
                      <tbody>
                        {a.credit_accounts.map((c, idx) => (
                          <tr key={idx} className="border-b border-slate-800">
                            <td className="py-1.5 px-2 font-medium">{c.creditor}</td>
                            <td className="py-1.5 px-2 text-xs">{c.account_type}</td>
                            <td className="py-1.5 px-2 text-xs font-mono text-slate-400">{c.account_number_masked || '—'}</td>
                            <td className="py-1.5 px-2 text-center text-xs">{c.open_date || '—'}</td>
                            <td className="py-1.5 px-2 text-center text-[10px] uppercase">{c.status}</td>
                            <td className="py-1.5 px-2 text-right">{R(c.balance)}</td>
                            <td className={`py-1.5 px-2 text-right ${c.arrears_amount > 0 ? 'text-rose-400 font-semibold' : 'text-slate-500'}`}>{c.arrears_amount > 0 ? R(c.arrears_amount) : '—'}</td>
                            <td className="py-1.5 px-2 text-center">{c.within_last_3_months ? <Badge className="text-[10px] bg-blue-950 border-blue-600 text-blue-300">RECENT</Badge> : <span className="text-slate-600">—</span>}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Section 5: QuinnPay Mandates */}
            {(a.quinnpay_mandates || []).length > 0 && (
              <Card className="bg-slate-800/50 border-slate-700">
                <CardHeader><CardTitle className="text-white text-lg flex items-center gap-2"><FileText className="h-5 w-5 text-amber-400" /> QuinnPay Mandates ({a.quinnpay_mandates.length})</CardTitle></CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm text-slate-200" data-testid="quinnpay-table">
                      <thead className="text-xs text-slate-400 border-b border-slate-700">
                        <tr>
                          <th className="text-left py-2 px-2">Lender</th>
                          <th className="text-center py-2 px-2">Status</th>
                          <th className="text-right py-2 px-2">Mandate</th>
                          <th className="text-center py-2 px-2">Collection Date</th>
                          <th className="text-right py-2 px-2">Overdue</th>
                          <th className="text-center py-2 px-2">✓ / ✗</th>
                        </tr>
                      </thead>
                      <tbody>
                        {a.quinnpay_mandates.map((q, idx) => (
                          <tr key={idx} className="border-b border-slate-800">
                            <td className="py-1.5 px-2 font-medium">{q.lender}</td>
                            <td className="py-1.5 px-2 text-center text-[10px] uppercase">{q.status}</td>
                            <td className="py-1.5 px-2 text-right">{R(q.mandate_amount)}</td>
                            <td className="py-1.5 px-2 text-center text-xs">{q.collection_date || '—'}</td>
                            <td className={`py-1.5 px-2 text-right ${q.overdue_amount > 0 ? 'text-rose-400 font-semibold' : 'text-slate-500'}`}>{q.overdue_amount > 0 ? R(q.overdue_amount) : '—'}</td>
                            <td className="py-1.5 px-2 text-center text-xs">
                              <span className="text-emerald-400">{q.successful_collections ?? 0}</span>
                              <span className="text-slate-500"> / </span>
                              <span className="text-rose-400">{q.failed_collections ?? 0}</span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Section 11: Competition Scan */}
            {((a.named_competitors_scan || []).length + (a.other_lenders_scan || []).length) > 0 && (
              <Card className="bg-slate-800/50 border-slate-700">
                <CardHeader><CardTitle className="text-white text-lg">Competition Scan</CardTitle></CardHeader>
                <CardContent className="space-y-4">
                  {(a.named_competitors_scan || []).length > 0 && (
                    <div>
                      <div className="text-xs uppercase text-slate-400 mb-1">Named Competitors ({a.named_competitors_scan.length})</div>
                      <table className="w-full text-sm text-slate-200" data-testid="named-competitors-table">
                        <thead className="text-xs text-slate-500 border-b border-slate-700">
                          <tr>
                            <th className="text-left py-1 px-2">Lender</th>
                            <th className="text-center py-1 px-2">Bank</th>
                            <th className="text-center py-1 px-2">Credit</th>
                            <th className="text-center py-1 px-2">QuinnPay</th>
                            <th className="text-center py-1 px-2">Recent Arrears</th>
                          </tr>
                        </thead>
                        <tbody>
                          {a.named_competitors_scan.map((n, i) => (
                            <tr key={i} className="border-b border-slate-800">
                              <td className="py-1 px-2">{n.lender}</td>
                              <td className="py-1 px-2 text-center">{n.sources?.bank ? '✓' : '—'}</td>
                              <td className="py-1 px-2 text-center">{n.sources?.credit ? '✓' : '—'}</td>
                              <td className="py-1 px-2 text-center">{n.sources?.quinnpay ? '✓' : '—'}</td>
                              <td className="py-1 px-2 text-center">{n.recent_arrears ? <span className="text-rose-400">{R(n.arrears_amount)}</span> : <span className="text-slate-600">—</span>}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                  {(a.other_lenders_scan || []).length > 0 && (
                    <div>
                      <div className="text-xs uppercase text-slate-400 mb-1">Other Lenders Detected ({a.other_lenders_scan.length})</div>
                      <table className="w-full text-sm text-slate-200" data-testid="other-lenders-table">
                        <thead className="text-xs text-slate-500 border-b border-slate-700">
                          <tr>
                            <th className="text-left py-1 px-2">Lender</th>
                            <th className="text-center py-1 px-2">Bank</th>
                            <th className="text-center py-1 px-2">Credit</th>
                            <th className="text-center py-1 px-2">QuinnPay</th>
                            <th className="text-center py-1 px-2">Recent Arrears</th>
                          </tr>
                        </thead>
                        <tbody>
                          {a.other_lenders_scan.map((n, i) => (
                            <tr key={i} className="border-b border-slate-800">
                              <td className="py-1 px-2">{n.lender}</td>
                              <td className="py-1 px-2 text-center">{n.sources?.bank ? '✓' : '—'}</td>
                              <td className="py-1 px-2 text-center">{n.sources?.credit ? '✓' : '—'}</td>
                              <td className="py-1 px-2 text-center">{n.sources?.quinnpay ? '✓' : '—'}</td>
                              <td className="py-1 px-2 text-center">{n.recent_arrears ? <span className="text-rose-400">{R(n.arrears_amount)}</span> : <span className="text-slate-600">—</span>}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Section 7: Unidentified Lenders & Full Transaction List for assessor override */}
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader><CardTitle className="text-white text-lg flex items-center gap-2"><HelpCircle className="h-5 w-5 text-amber-400" /> Transactions — Assessor Review</CardTitle></CardHeader>
              <CardContent>
                <p className="text-xs text-slate-500 mb-3">Reclassify or rename any transaction. Changes recompute the full assessment automatically.</p>
                <div className="overflow-x-auto max-h-[500px] overflow-y-auto">
                  <table className="w-full text-sm text-slate-200" data-testid="transactions-table">
                    <thead className="text-xs text-slate-400 border-b border-slate-700 sticky top-0 bg-slate-800">
                      <tr>
                        <th className="text-left py-2 px-2">Date</th>
                        <th className="text-left py-2 px-2">Description</th>
                        <th className="text-right py-2 px-2">Amount</th>
                        <th className="text-center py-2 px-2">Type</th>
                        <th className="text-left py-2 px-2 w-48">Classification</th>
                        <th className="text-left py-2 px-2 w-40">Lender</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(analysis.transactions || []).map(t => (
                        <TxnRow key={t.id} t={t} overrides={caseData.overrides || {}} onSave={overrideTxn} />
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>

            {/* Section 12: Final Report Block */}
            {a.final_report && (
              <Card className={`bg-slate-900/70 ${style.ring} border-2 print:break-before-page`}>
                <CardHeader><CardTitle className="text-white text-lg">Final Report — Borrowing Profile</CardTitle></CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                    <FinalStat label="Total Lenders Detected" value={a.final_report.total_lenders} />
                    <FinalStat label="Active Lenders" value={a.final_report.active_lenders} />
                    <FinalStat label="Loan Deposits" value={a.final_report.loan_deposits} />
                    <FinalStat label="Total Loan Deposit Value" value={R(a.final_report.total_loan_deposit_value)} />
                    <FinalStat label="Monthly Bank Loan Repayments" value={R(a.final_report.monthly_bank_loan_repayments)} />
                    <FinalStat label="Post-Salary Loans" value={a.final_report.post_salary_loans} />
                    <FinalStat label="Loans Before Payday" value={a.final_report.loans_before_payday} />
                    <FinalStat label="Unidentified Lenders" value={a.final_report.unidentified_lenders_count} />
                    <FinalStat label="Loan Stacking" value={a.final_report.loan_stacking ? 'YES' : 'NO'} />
                    <FinalStat label="Borrowing Activity" value={a.final_report.borrowing_activity} />
                    <FinalStat label="Borrowing Risk" value={a.final_report.borrowing_risk} />
                  </div>
                </CardContent>
              </Card>
            )}
          </>
        ) : (
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-8 text-center text-slate-400">
              <RefreshCw className="h-10 w-10 mx-auto mb-2 opacity-40" />
              <p>Upload bank statements, then click <span className="text-emerald-400">Run Analysis</span> to detect loans.</p>
              <p className="text-xs text-slate-500 mt-1">Analysis typically takes 30–90 s depending on the number of statement pages.</p>
            </CardContent>
          </Card>
        )}

        {/* Assessor Notes */}
        <NotesPanel notes={caseData.assessor_notes || []} onAdd={addNote} onRemove={removeNote} />

        {/* Audit log */}
        <AuditPanel entries={caseData.audit_log || []} />
      </div>
    </DashboardLayout>
  );
}

function NotesPanel({ notes, onAdd, onRemove }) {
  const [draft, setDraft] = useState('');
  const submit = () => {
    if (!draft.trim()) return;
    onAdd(draft);
    setDraft('');
  };
  return (
    <Card className="bg-slate-800/50 border-slate-700" data-testid="notes-panel">
      <CardHeader><CardTitle className="text-white text-lg flex items-center gap-2"><MessageSquare className="h-5 w-5 text-emerald-400" /> Assessor Notes ({notes.length})</CardTitle></CardHeader>
      <CardContent>
        <div className="flex gap-2 mb-3">
          <Input value={draft} onChange={e => setDraft(e.target.value)} onKeyDown={e => e.key === 'Enter' && submit()} placeholder="Add a note about this applicant…" className="bg-slate-900 border-slate-700 text-white" data-testid="note-input" />
          <Button onClick={submit} className="bg-emerald-600 hover:bg-emerald-700" data-testid="add-note-btn">Add</Button>
        </div>
        {notes.length === 0 ? <div className="text-xs text-slate-500 text-center py-2">No notes yet.</div>
          : (
            <ul className="space-y-2">
              {notes.slice().reverse().map(n => (
                <li key={n.id} className="bg-slate-900 border border-slate-700 rounded p-2 flex items-start gap-2" data-testid={`note-${n.id}`}>
                  <div className="flex-1">
                    <div className="text-[10px] text-slate-500">{(n.at || '').substring(0, 19).replace('T', ' ')} · {n.by}</div>
                    <div className="text-sm text-slate-200 whitespace-pre-wrap">{n.text}</div>
                  </div>
                  <Button size="sm" variant="destructive" className="h-6 w-6 p-0" onClick={() => onRemove(n.id)} data-testid={`remove-note-${n.id}`}><Trash2 className="h-3 w-3" /></Button>
                </li>
              ))}
            </ul>
          )}
      </CardContent>
    </Card>
  );
}

function AuditPanel({ entries }) {
  const [open, setOpen] = useState(false);
  return (
    <Card className="bg-slate-800/50 border-slate-700" data-testid="audit-panel">
      <CardHeader className="cursor-pointer" onClick={() => setOpen(o => !o)}>
        <CardTitle className="text-white text-lg flex items-center gap-2">
          <History className="h-5 w-5 text-slate-400" /> Audit Log ({entries.length}) <span className="text-xs text-slate-500 ml-auto">{open ? 'hide' : 'show'}</span>
        </CardTitle>
      </CardHeader>
      {open && (
        <CardContent>
          {entries.length === 0 ? <div className="text-xs text-slate-500 text-center py-2">No audit entries yet.</div>
            : (
              <ul className="space-y-1 max-h-80 overflow-y-auto">
                {entries.slice().reverse().map((e, i) => (
                  <li key={i} className="text-xs text-slate-300 border-b border-slate-800 pb-1" data-testid={`audit-entry-${i}`}>
                    <span className="text-slate-500 font-mono">{(e.at || '').substring(0, 19).replace('T', ' ')}</span>
                    <span className="text-emerald-400 ml-2 font-semibold">{e.action}</span>
                    <span className="text-slate-400 ml-2">by {e.actor}</span>
                    {e.details && <span className="text-slate-500 ml-2 truncate">— {e.details}</span>}
                  </li>
                ))}
              </ul>
            )}
        </CardContent>
      )}
    </Card>
  );
}

function FinalStat({ label, value }) {
  return (
    <div className="bg-slate-800/60 border border-slate-700 rounded p-3">
      <div className="text-[10px] uppercase tracking-wider text-slate-400">{label}</div>
      <div className="text-white text-lg font-semibold mt-1">{value}</div>
    </div>
  );
}

function MonthlyLoanChart({ inflows, repayments }) {
  // Aggregate inflows + repayments per YYYY-MM
  const byMonth = {};
  const addTo = (dateStr, key, amount) => {
    if (!dateStr || dateStr.length < 7) return;
    const m = dateStr.substring(0, 7);
    byMonth[m] = byMonth[m] || { month: m, inflows: 0, repayments: 0 };
    byMonth[m][key] += Number(amount) || 0;
  };
  inflows.forEach(i => addTo(i.deposit_date, 'inflows', i.amount));
  repayments.forEach(r => { if (r.successful) addTo(r.debit_date, 'repayments', r.amount); });
  const data = Object.values(byMonth).sort((a, b) => a.month.localeCompare(b.month));
  if (data.length === 0) return null;
  return (
    <Card className="bg-slate-800/50 border-slate-700" data-testid="monthly-loan-chart">
      <CardHeader><CardTitle className="text-white text-lg flex items-center gap-2"><BarChart3 className="h-5 w-5 text-emerald-400" /> Monthly Loan Activity</CardTitle></CardHeader>
      <CardContent>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data} margin={{ top: 10, right: 10, bottom: 5, left: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
              <XAxis dataKey="month" stroke="#94a3b8" fontSize={12} />
              <YAxis stroke="#94a3b8" fontSize={12} tickFormatter={(v) => `R${v / 1000}k`} />
              <Tooltip
                contentStyle={{ background: '#0f172a', border: '1px solid #334155', borderRadius: 6 }}
                labelStyle={{ color: '#f1f5f9' }}
                formatter={(v) => `R ${Number(v).toLocaleString()}`}
              />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="inflows" name="Loan Inflows" fill="#10b981" radius={[4, 4, 0, 0]}>
                {data.map((_, i) => <Cell key={i} fill="#10b981" />)}
              </Bar>
              <Bar dataKey="repayments" name="Loan Repayments" fill="#f43f5e" radius={[4, 4, 0, 0]}>
                {data.map((_, i) => <Cell key={i} fill="#f43f5e" />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
}

function TxnRow({ t, overrides, onSave }) {
  const override = overrides[t.id] || {};
  const [cls, setCls] = useState(override.classification || t.classification);
  const [lender, setLender] = useState(override.lender_name ?? t.lender_name ?? '');
  const [saving, setSaving] = useState(false);
  const dirty = cls !== (override.classification || t.classification) ||
                (lender || '') !== ((override.lender_name ?? t.lender_name) || '');
  const save = async () => {
    setSaving(true);
    await onSave(t.id, { classification: cls, lender_name: lender });
    setSaving(false);
  };
  return (
    <tr className="border-b border-slate-800" data-testid={`txn-row-${t.id}`}>
      <td className="py-1.5 px-2 text-xs">{t.date}</td>
      <td className="py-1.5 px-2 text-xs text-slate-300 truncate max-w-[280px]">{t.description}</td>
      <td className="py-1.5 px-2 text-right">{R(t.amount)}</td>
      <td className="py-1.5 px-2 text-center">
        <Badge className={t.type === 'credit' ? 'bg-emerald-950 border-emerald-600 text-emerald-300 text-[10px]' : 'bg-rose-950 border-rose-600 text-rose-300 text-[10px]'}>{t.type}</Badge>
      </td>
      <td className="py-1.5 px-2">
        <Select value={cls} onValueChange={setCls}>
          <SelectTrigger className="h-7 bg-slate-900 border-slate-700 text-white text-xs" data-testid={`txn-class-${t.id}`}><SelectValue /></SelectTrigger>
          <SelectContent>{CLASSIFICATION_OPTIONS.map(o => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}</SelectContent>
        </Select>
      </td>
      <td className="py-1.5 px-2 flex items-center gap-1">
        <Input value={lender || ''} onChange={e => setLender(e.target.value)} className="h-7 bg-slate-900 border-slate-700 text-white text-xs" placeholder="Lender" data-testid={`txn-lender-${t.id}`} />
        {dirty && <Button size="sm" onClick={save} disabled={saving} className="h-7 px-2 bg-emerald-600 hover:bg-emerald-700 text-xs" data-testid={`txn-save-${t.id}`}>{saving ? '…' : 'Save'}</Button>}
      </td>
    </tr>
  );
}
